import React from "react"
import styles from "./EmployeeTable.module.scss"
import { groupBy } from "lodash"
import { RoleAccessSnapshotType } from "../models/access-store"
import { Menu, MenuItem } from "@blueprintjs/core"
import { showContextMenu } from "modules/common/services/context-menu"
import { texts } from "modules/common/texts"
import { EmptySquare } from "modules/common/components/icons/square"
import { RemoveConfirmation } from "modules/common/components/form/RemoveConfirmation"

export const W = 45
export const H = 25

export const calculateWidth = (span: number): React.CSSProperties => ({
    maxWidth: `${W * span}px`,
    minWidth: `${W * span}px`,
    width: `${W * span}px`,
})

export const calculateHeight = (): React.CSSProperties => ({
    maxHeight: `${H}px`,
    minHeight: `${H}px`,
    height: `${H}px`,
})

export const EmployeeTableHeader = ({
    data,
    onCopy,
    buffer,
    onPaste,
    selectedIndex,
    onSelect,
}: EmployeeTableHeaderProps) => {
    // Группируем роли по отделам
    const departments = groupBy(data, (e) => e.department || "Без отдела")

    // Сохраняем порядок отделов из оригинального массива
    const departmentOrder: string[] = []
    data.forEach((role) => {
        const dept = role.department || "Без отдела"
        if (!departmentOrder.includes(dept)) {
            departmentOrder.push(dept)
        }
    })

    return (
        <div>
            <table className={`${styles.table} ${styles.header}`}>
                <thead>
                    {/* Строка с названиями отделов */}
                    <tr>
                        {departmentOrder.map((dep) => {
                            const span = departments[dep].length
                            const style = calculateWidth(span)

                            return (
                                <th key={dep} colSpan={span} style={style} className={styles.department}>
                                    <div>{dep}</div>
                                </th>
                            )
                        })}
                    </tr>

                    {/* Строка с названиями ролей */}
                    <tr>
                        {departmentOrder.map((dep) => (
                            departments[dep].map((role) => {
                                const originalIndex = data.findIndex((r) => r.id === role.id)
                                return (
                                    <EmployerName
                                        key={role.id}
                                        role={role}
                                        onCopy={onCopy}
                                        buffer={buffer}
                                        onPaste={onPaste}
                                        index={originalIndex}
                                        selectedIndex={selectedIndex}
                                        onClick={() => onSelect(originalIndex)}
                                    />
                                )
                            })
                        ))}
                    </tr>
                </thead>
            </table>
        </div>
    )
}

// Декоратор @ContextMenuTarget убран из Blueprint начиная с 4-й версии.
// Меню показываем сами: обработчик на той же ячейке, разметка таблицы не меняется
// (обёртка <ContextMenu> вставила бы <div> внутрь <tr>).
class EmployerName extends React.PureComponent<EmployerNameProps> {
    private confirmRemoving: any = null;

    render() {
        const { role: employer, onClick, index, selectedIndex } = this.props
        const current = selectedIndex === index ? styles.current : ""
        const columnOpener = index === selectedIndex && index === 0 ? styles.columnOpener : ""
        const columnSelection = index === selectedIndex ? styles.columnSelection : ""

        return (
            <th
                key={employer.id}
                className={`${styles.role} ${columnSelection} ${columnOpener} ${current}`}
                onClick={onClick}
                onContextMenu={this.showContextMenu}
            >
                <RemoveConfirmation<RoleAccessSnapshotType>
                    what={(access) => `доступ для ${access.name}`}
                    onConfirmed={(access) => this.clearConfirmed(access)}
                    render={({ confirmRemoving }) => {
                        this.confirmRemoving = confirmRemoving
                        return <div className={styles.rotated}>{employer.name}</div>
                    }}
                />
            </th>
        )
    }

    showContextMenu = (e: React.MouseEvent) => {
        e.preventDefault()

        showContextMenu({
            content: this.renderContextMenu(),
            targetOffset: { left: e.clientX, top: e.clientY },
        })
    };

    renderContextMenu() {
        const { buffer, role: employer } = this.props

        return (
            <Menu>
                <MenuItem onClick={this.copy} text={texts.copy} icon="duplicate" />
                {buffer && <MenuItem onClick={this.paste} text={texts.paste} icon="arrow-down" />}
                {employer.access.length > 0 && (
                    <MenuItem onClick={this.clear} text={`${texts.remove} доступ`} icon={<EmptySquare />} />
                )}
            </Menu>
        )
    }

    copy = () => {
        const { role, onCopy } = this.props
        onCopy([...role.access.map((a) => a.name)])
    };

    paste = () => {
        const { onPaste, buffer, role } = this.props
        buffer && onPaste(role.id, buffer)
    };

    clear = () => this.confirmRemoving && this.confirmRemoving(this.props.role);

    clearConfirmed = (role: RoleAccessSnapshotType) => {
        const { onPaste } = this.props
        onPaste(role.id, [])
    };
}

interface Buffer {
    onCopy: (access: string[]) => void
    onPaste: (roleId: string, access: string[]) => void
    buffer: string[] | null
}

interface EmployerNameProps extends Buffer {
    role: RoleAccessSnapshotType
    index: number
    selectedIndex: number
    onClick: () => void
}

interface EmployeeTableHeaderProps extends Buffer {
    data: RoleAccessSnapshotType[]
    selectedIndex: number
    onSelect: (index: number) => void
}